import { EventEmitter } from "events";
import moment from "moment-timezone";

const events = {
  PLAYING_CHANGED: "playingchanged",
  TIME_CHANGED: "timechanged",
  TIME_BOUNDS_CHANGED: "timeboundschanged",
  TIME_INTERVAL_CHANGED: "timeintervalchanged",
  REALTIME_TRACKING_CHANGED: "realtimetrackingchanged",
};

export default class TimeManager extends EventEmitter {
  /**
   * @constructor
   * @param {moment} start_date - Starting date of loop
   * @param {moment} end_date - Ending date of loop
   * @param {moment.duration} time_interval - Interval between loop steps
   * @param {integer} options - Object containing:
   *                               time: time between frames
   *                               dwell: time to dwell at end of loop
   */
  constructor(start_date, end_date, time_interval, loop_options) {
    super();
    this._start_date = this._momentify(start_date);
    this._end_date = this._momentify(end_date);
    this._interval = time_interval;
    this._pointer = this._end_date;

    this._loop_options = Object.assign(
      {
        time: 1000,
        dwell: 3000,
      },
      loop_options
    );

    this._playing = false;
    this._forward_direction = true;
    this._tracking = false;
    this._tracking_timerId = null;
  }

  get start_date() {
    return this._start_date;
  }

  set start_date(new_date) {
    if (new_date <= this._end_date) {
      this._start_date = new_date;
      setTimeout(
        () =>
          this.emit(events.TIME_BOUNDS_CHANGED, {
            start: this._start_date,
            end: this._end_date,
          }),
        0
      );
      this._ensure_pointer_in_range();
    }
    return this._start_date;
  }

  get end_date() {
    return this._end_date;
  }

  set end_date(new_date) {
    if (new_date >= this._start_date) {
      this._end_date = new_date;
      setTimeout(
        () =>
          this.emit(events.TIME_BOUNDS_CHANGED, {
            start: this._start_date,
            end: this._end_date,
          }),
        0
      );
      this._ensure_pointer_in_range();
    }
    return this._end_date;
  }

  set_range(new_start_date, new_end_date) {
    if (new_start_date <= new_end_date) {
      this._start_date = new_start_date;
      this._end_date = new_end_date;
      setTimeout(
        () =>
          this.emit(events.TIME_BOUNDS_CHANGED, {
            start: this._start_date,
            end: this._end_date,
          }),
        0
      );
      this._ensure_pointer_in_range();
    }
  }

  // Starts tracking realtime. The time manager's end_time will be
  // set to the current time and will update update_frequency seconds.
  // The start_time will be offset from the end_time by the given delta.
  start_tracking(delta, update_frequency) {
    const oldTracking = this._tracking;
    this._tracking = true;
    if (!oldTracking) {
      setTimeout(() => this.emit(events.REALTIME_TRACKING_CHANGED, true), 0);
    }
    this._tracking_delta = delta;
    this._tracking_frequency = moment.isDuration(update_frequency)
      ? update_frequency
      : moment.duration(update_frequency);
    this._tracking_update();
  }

  stop_tracking() {
    const oldTracking = this._tracking;
    this._tracking = false;
    if (this._tracking_timerId !== null) {
      clearTimeout(this._tracking_timerId);
      this._tracking_timerId = null;
    }
    if (oldTracking) {
      setTimeout(() => this.emit(events.REALTIME_TRACKING_CHANGED, false), 0);
    }
  }

  _tracking_update() {
    if (this._tracking) {
      const now = moment();
      const start = now.clone().subtract(this._tracking_delta);
      this.set_range(start, now);
      if (this._tracking_timerId) {
        clearTimeout(this._tracking_timerId);
      }
      this._tracking_timerId = setTimeout(
        this._tracking_update.bind(this),
        this._tracking_frequency
      );
    }
  }

  get interval() {
    return this._interval;
  }

  set interval(new_interval) {
    this._interval = new_interval;
    setTimeout(
      () => this.emit(events.TIME_INTERVAL_CHANGED, this._interval),
      0
    );
    return this._interval;
  }

  get current_time() {
    return this._pointer;
  }

  set current_time(new_date) {
    if (moment.isMoment(new_date)) {
      this._update_pointer(new_date);
    }
  }

  get tracking() {
    return this._tracking;
  }

  get loop_options() {
    return this._loop_options;
  }

  set_loop_options(new_options) {
    this._loop_options = Object.assign(this._loop_options, new_options);
  }

  _momentify(date) {
    if (moment.isMoment(date)) return date;
    return moment(date);
  }

  _ensure_pointer_in_range() {
    if (this._pointer < this._start_date) {
      this._update_pointer(this._start_date);
    }
    if (this._pointer > this._end_date) {
      this._update_pointer(this._end_date);
    }
  }

  _update_pointer(new_date) {
    let old_date = this._pointer;
    this._pointer = new_date;
    if (!old_date.isSame(new_date)) {
      setTimeout(() => this.emit(events.TIME_CHANGED, new_date, old_date), 0);
    }
  }

  _advance() {
    let delay = this._loop_options.time;
    if (this._forward_direction) {
      this.step_forward();
      if (this._pointer + this.interval > this.end_date) {
        delay = this._loop_options.dwell;
      }
    } else {
      this.step_back();
      if (this._pointer - this.interval < this.start_date) {
        delay = this._loop_options.dwell;
      }
    }
    if (this._playing) {
      this._interval_handler = setTimeout(this._advance.bind(this), delay);
    }
  }

  isPlaying() {
    return this._playing;
  }

  play(forward_direction = true) {
    this._forward_direction = forward_direction;
    if (!this._playing) {
      this._playing = true;
      this._advance();
      setTimeout(() => this.emit(events.PLAYING_CHANGED, true), 0);
    }
  }

  stop() {
    if (this._playing) {
      this._playing = false;
      if (this._interval_handler) {
        clearTimeout(this._interval_handler);
      }
      this._interval_handler = null;
      setTimeout(() => this.emit(events.PLAYING_CHANGED, false), 0);
    }
  }

  step_forward() {
    let new_pointer = this._pointer.clone().add(this.interval);
    this._update_pointer(
      new_pointer > this.end_date ? this.start_date : new_pointer
    );
  }

  step_back() {
    let new_pointer = this._pointer.clone().subtract(this.interval);
    this._update_pointer(
      new_pointer < this.start_date ? this.end_date : new_pointer
    );
  }
}

export { events };
